import * as CryptoJS from 'crypto-js';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

// ─────────────────────────────────────────────────────────────────────────────
// SENSITIVE KEY REGISTRY
//
// Any localStorage key whose value must be stored encrypted at rest and
// returned decrypted on every read — by EVERY caller in the app.
//
// If you add a new key that holds sensitive data, register it here.
// ─────────────────────────────────────────────────────────────────────────────
// Capture native methods BEFORE the IIFE patch below overwrites them.
// SecureStorageService uses these so it can encrypt once and write directly
// to raw storage — bypassing the patch to prevent double-encryption.
const _nativeSet = Storage.prototype.setItem.bind(localStorage);
const _nativeGet = Storage.prototype.getItem.bind(localStorage);
const _nativeRemove = Storage.prototype.removeItem.bind(localStorage);

export const SENSITIVE_STORAGE_KEYS = new Set<string>([
  // Auth tokens
  'token',
  'vegitoken',
  'Tokens',
  'adalAccessToken',
  // User identifiers
  'claId',
  'claAdminId',
  'userId',
  // User profile & roles
  'isAdmin',
  'userType',
  'userDetails',
  'selectedMember',
  'hasPriviledge',
  // PII
  'loginemailaddress',
  'userEmail',
  'emailId',
  'hostEmailid',
  // Permissions
  'claAdminPermissions',
  // Location / org
  'parkId',
  'parkDropDownDetails',
  'tenantId',
  'locationId',
  // Meeting / booking context
  'selectedHost',
  // Navigation state carrying auth context
  'redirectType',
]);

// ─────────────────────────────────────────────────────────────────────────────
// MODULE-LEVEL PATCH — runs at JavaScript import time, before Angular
// bootstraps, before any class is instantiated, before any field initializer
// executes.
//
// WHY HERE AND NOT IN A CONSTRUCTOR:
//   Angular services are lazily instantiated. Class-level field initializers
//   (e.g. sidenav.service.ts lines 9-10) execute before any constructor runs,
//   so a constructor-based patch is always too late for those consumers.
//   An IIFE at module scope runs the instant this file is imported — which
//   happens synchronously during the initial bundle evaluation, before any
//   Angular component or service is created.
//
// SAFETY GUARDS:
//   • Only keys listed in SENSITIVE_STORAGE_KEYS are encrypted/decrypted.
//     All other keys (MSAL cache, UI state, third-party libs) pass through.
//   • A __cla_patched marker on Storage.prototype prevents double-patching
//     (hot module replacement, test runners, etc.).
//   • try/catch in getItem returns the raw stored value if decryption fails,
//     ensuring backward compatibility with pre-encryption sessions.
// ─────────────────────────────────────────────────────────────────────────────
(function applySecureStoragePatch() {
  if ((Storage.prototype as any).__cla_patched) return;
  (Storage.prototype as any).__cla_patched = true;

  const ENCRYPTION_KEY: string = environment.storageEncryptionKey;
  const KEYS = SENSITIVE_STORAGE_KEYS;

  const nativeSet = Storage.prototype.setItem;
  const nativeGet = Storage.prototype.getItem;

  Storage.prototype.setItem = function patchedSetItem(
    storageKey: string,
    value: string
  ): void {
    // Native localStorage.setItem coerces any value to a string via String().
    // We must do the same before passing to CryptoJS, otherwise booleans,
    // numbers, null, or undefined reach AES.encrypt as non-strings and
    // trigger RangeError: Invalid array length inside CryptoJS internals.
    const strValue: string = (value === null || value === undefined)
      ? ''
      : String(value);

    if (KEYS.has(storageKey)) {
      nativeSet.call(
        this,
        storageKey,
        CryptoJS.AES.encrypt(strValue, ENCRYPTION_KEY).toString()
      );
    } else {
      nativeSet.call(this, storageKey, strValue);
    }
  };

  Storage.prototype.getItem = function patchedGetItem(
    storageKey: string
  ): string | null {
    const stored = nativeGet.call(this, storageKey);

    // Not a sensitive key or nothing stored — return as-is.
    if (stored === null || stored === undefined || !KEYS.has(storageKey)) {
      return stored ?? null;
    }

    try {
      const bytes = CryptoJS.AES.decrypt(stored, ENCRYPTION_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      // An empty decrypted string is only "plaintext fallback" when `stored`
      // never was ciphertext to begin with. Genuine ciphertext (CryptoJS's
      // salted AES format, base64-prefixed "U2FsdGVkX1") can legitimately
      // decrypt to '' — e.g. an intentionally empty value that was encrypted
      // at write time — and must not fall back to the raw ciphertext.
      return stored.startsWith('U2FsdGVkX1') ? decrypted : (decrypted || stored);
    } catch {
      // Decryption error: value was stored before encryption was introduced.
      return stored;
    }
  };
})();

// ─────────────────────────────────────────────────────────────────────────────
// ANGULAR SERVICE — encrypts sensitive keys explicitly before writing to
// localStorage and decrypts them on read. This mirrors the IIFE patch above
// so that static-analysis tools (GHAS / CodeQL) can verify that no sensitive
// value is ever written to storage in plaintext.
// ─────────────────────────────────────────────────────────────────────────────
@Injectable({
  providedIn: 'root',
})
export class SecureStorageService {

  private readonly encryptionKey: string = environment.storageEncryptionKey;

  setItem(storageKey: string, value: string): void {
    const strValue = (value === null || value === undefined) ? '' : String(value);
    if (SENSITIVE_STORAGE_KEYS.has(storageKey)) {
      // Encrypt then write via native method to avoid the IIFE patch encrypting again.
      _nativeSet(storageKey, CryptoJS.AES.encrypt(strValue, this.encryptionKey).toString());
    } else {
      _nativeSet(storageKey, strValue);
    }
  }

  getItem(storageKey: string): string | null {
    // Read via native method to get the raw encrypted bytes, then decrypt once.
    const stored = _nativeGet(storageKey);
    if (stored === null || !SENSITIVE_STORAGE_KEYS.has(storageKey)) {
      return stored;
    }
    try {
      const decrypted = CryptoJS.AES.decrypt(stored, this.encryptionKey).toString(CryptoJS.enc.Utf8);
      // See patchedGetItem above for why truthiness alone can't gate the
      // plaintext fallback — genuine ciphertext can legitimately decrypt to ''.
      return stored.startsWith('U2FsdGVkX1') ? decrypted : (decrypted || stored);
    } catch {
      return stored;
    }
  }

  removeItem(storageKey: string): void {
    _nativeRemove(storageKey);
  }

  clear(): void {
    localStorage.clear();
  }
}
